import { Link } from 'react-router-dom'
import { Calendar, ArrowRight, ShieldCheck, CheckCircle2 } from 'lucide-react'
import { scrapedData } from '../data'
import { renderFormattedText } from '../utils/textFormatter'
import SEO from '../components/SEO'

export default function RorbaekTeknikken() {
  const { rorbaekTeknikken, therapists } = scrapedData

  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Rørbæk Teknikken | Manuel Helhedsbehandling i Viborg"
        description="Lær om Rørbæk Teknikken – vores specialudviklede behandlingsmetode, der kombinerer bindevævsbehandling, muskelrelease og dybdeterapi til varig smertelindring i Viborg."
        ogImage={rorbaekTeknikken.image}
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Rørbæk Teknikken
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Vores egen specialiserede behandlingsmetode, udviklet gennem mange års erfaring med svære smerte- og spændingstilstande.
          </p>
        </div>
      </section>

      {/* Main Presentation & Verbatim Text */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Image & Booking Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-md border border-[#E6DFD3] aspect-[4/3]">
              <img
                src={rorbaekTeknikken.image}
                alt="Rørbæk Teknikken behandling"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Booking Card */}
            <div className="bg-[#F5F0E6] rounded-xl p-6 border border-[#E6DFD3] space-y-4">
              <h3 className="font-serif text-lg font-bold text-[#1E352F] border-b border-[#E6DFD3] pb-2">
                Bestil tid online
              </h3>
              <p className="text-xs sm:text-sm text-[#1E352F]/70 leading-relaxed">
                Book din næste behandling nemt og hurtigt via vores Planway online booking.
              </p>
              <div className="pt-2">
                <a
                  href="https://roerbaekklinikken.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#eebb53] hover:bg-[#dca63d] text-white py-3 px-6 rounded-full text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm"
                >
                  <Calendar className="w-4 h-4" />
                  Gå til online booking
                </a>
              </div>
            </div>
          </div>

          {/* Right: Verbatim Text */}
          <div className="lg:col-span-7 space-y-6">
            <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
              {rorbaekTeknikken.title}
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
              {renderFormattedText(rorbaekTeknikken.content)}
            </div>
          </div>

        </div>
      </section>

      {/* Presenting Therapists */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-[#E6DFD3] pt-16 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
            Behandlere tilknyttet Rørbæk Teknikken
          </h2>
          <p className="text-sm sm:text-base text-[#1E352F]/70">
            Du kan møde følgende erfarne behandlere på klinikken, der alle arbejder med det hele menneske som fælles udgangspunkt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {therapists.map((therapist, idx) => (
            <div
              key={therapist.name}
              className={`bg-white rounded-2xl border border-[#E6DFD3] p-6 sm:p-8 shadow-sm flex flex-col items-center text-center space-y-4 ${
                idx === 2 ? 'md:col-span-12 lg:col-span-4' : 'md:col-span-6 lg:col-span-4'
              }`}
            >
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#F5F0E6] shadow-sm">
                <img
                  src={therapist.image}
                  alt={therapist.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-1">
                <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                  {therapist.name}
                </h3>
                <p className="text-xs font-semibold text-[#eebb53] uppercase tracking-wider">
                  {therapist.role}
                </p>
              </div>
              <div className="w-full pt-4 border-t border-[#F5F0E6] space-y-2">
                <p className="text-xs font-bold text-[#1E352F]/60 uppercase tracking-wider">Specialer:</p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {therapist.specialties.slice(0, 4).map((spec) => (
                    <span
                      key={spec}
                      className="text-[10px] font-medium bg-[#F5F0E6]/60 text-[#1E352F]/80 px-2 py-0.5 rounded-full"
                    >
                      {spec}
                    </span>
                  ))}
                  {therapist.specialties.length > 4 && (
                    <span className="text-[10px] font-bold text-[#eebb53] px-2 py-0.5">
                      +{therapist.specialties.length - 4} mere
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43]">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
            Klar til at opleve en mærkbar forskel?
          </h2>
          <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
            Tilmeld dig de mange tilfredse klienter i Viborg, der har fået hjælp til at slippe af med smerter og spændinger. Book din tid online i dag.
          </p>
          <div className="pt-2">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
            >
              <Calendar className="w-5 h-5" />
              Book tid online
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}
